<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { richTextToGovukHtml } from '/@src/utils/richTextToGovukHtml'
import {
  ZoppmPodmienkaPublicResponse,
  ZoppmPodmienkaSpecRefTypEnum,
} from '/@src/client-public'
import { computed } from 'vue'

export interface PodmienkyPoskytnutiaProps {
  title: string
  data?: ZoppmPodmienkaPublicResponse
  /** Pozicia v poli `podmienkyPoskytnutiaPrispevku` - nutne kvoli tvorbe name pri validacii. */
  arrayPosition?: number
  specRefName: ZoppmPodmienkaSpecRefTypEnum
}

const props = defineProps<PodmienkyPoskytnutiaProps>()

const { t } = useI18n()

enum ZoPPMPodmienkaStruktVyjadrenie {
  splnam = 'ANO',
  nesplnam = 'NIE',
  netykaSa = 'NETYKA_SA',
}

const VYJADRENIE_OPTIONS = [
  { value: ZoPPMPodmienkaStruktVyjadrenie.splnam, label: t('zoppm.step2.splnam') },
  { value: ZoPPMPodmienkaStruktVyjadrenie.nesplnam, label: t('zoppm.step2.nesplnam') },
  { value: ZoPPMPodmienkaStruktVyjadrenie.netykaSa, label: t('zoppm.step2.netykaSa') },
]

const strukturovanePodmienky = computed(() =>
  props.data ? props.data?.strukturovanePodmienky : {}
)
const zoppmPodmienkaSpecRef = computed(() =>
  props.data ? props.data?.zoppmPodmienkaSpecRef : {}
)
</script>

<template>
  <div class="mb-8" v-html="richTextToGovukHtml(data?.zoppmPodmienkaSpecRef.podmienka)" />

  <ValidationWrapper
    v-slot="slotProps"
    :name="`podmienkyPoskytnutiaPrispevku[${arrayPosition}].zoppmPodmienkaSpecRef.zoppmSpecId`"
    :value="zoppmPodmienkaSpecRef.zoppmSpecId"
  >
    <IDSKInput v-bind="slotProps" readonly hidden />
  </ValidationWrapper>
  <ValidationWrapper
    v-slot="slotProps"
    :name="`podmienkyPoskytnutiaPrispevku[${arrayPosition}].zoppmPodmienkaSpecRef.zoppmPodmienkaSpecId`"
    :value="zoppmPodmienkaSpecRef.zoppmPodmienkaSpecId"
  >
    <IDSKInput v-bind="slotProps" readonly hidden />
  </ValidationWrapper>
  <ValidationWrapper
    v-slot="slotProps"
    :name="`podmienkyPoskytnutiaPrispevku[${arrayPosition}].zoppmPodmienkaSpecRef.typ`"
    :value="zoppmPodmienkaSpecRef.typ"
  >
    <IDSKInput v-bind="slotProps" readonly hidden />
  </ValidationWrapper>
  <ValidationWrapper
    v-slot="slotProps"
    :name="`podmienkyPoskytnutiaPrispevku[${arrayPosition}].zoppmPodmienkaSpecRef.podmienka`"
    :value="zoppmPodmienkaSpecRef.podmienka"
  >
    <IDSKInput v-bind="slotProps" readonly hidden />
  </ValidationWrapper>

  <template v-for="(item, key) in strukturovanePodmienky" :key="key">
    <IDSKHeading as="h3" variant="m" class="mb-0">
      {{ key + 1 }}. {{ item?.strukturovanaPodmienkaSpecRef?.nazov }}
    </IDSKHeading>
    <ValidationWrapper
      v-slot="slotProps"
      :name="`podmienkyPoskytnutiaPrispevku[${arrayPosition}].strukturovanePodmienky[${key}].zoppmStruktPodmienkaSpecRef`"
      :value="item?.strukturovanaPodmienkaSpecRef"
    >
      <IDSKInput v-bind="slotProps" readonly hidden />
    </ValidationWrapper>

    <ValidationWrapper
      v-slot="{ modelValue, ...slotProps }"
      :name="`podmienkyPoskytnutiaPrispevku[${arrayPosition}].strukturovanePodmienky[${key}].vyjadrenie`"
      :value="ZoPPMPodmienkaStruktVyjadrenie.netykaSa"
    >
      <IDSKRadios
        :id="`podmienkyPoskytnutiaPrispevku[${arrayPosition}].strukturovanePodmienky[${key}].vyjadrenie`"
        v-bind="slotProps"
        :options="VYJADRENIE_OPTIONS"
        :default-value="ZoPPMPodmienkaStruktVyjadrenie.netykaSa"
        :model-value="modelValue"
        class="mb-0"
        radios-wrapper-class="mb-1"
      />
    </ValidationWrapper>
    <ValidationWrapper
      v-slot="slotProps"
      :name="`podmienkyPoskytnutiaPrispevku[${arrayPosition}].strukturovanePodmienky[${key}].poznamka`"
    >
      <IDSKTextarea v-bind="slotProps" :label="t('zoppm.step2.poznamka')" class="mb-2" />
    </ValidationWrapper>

    <div class="mb-9">
      <IDSKHeading as="h4" variant="m" class="mb-2">{{
        t('zoppm.step2.prilohy')
      }}</IDSKHeading>
      <Prilohy
        :name="`podmienkyPoskytnutiaPrispevku[${arrayPosition}]`"
        :array-position="arrayPosition"
        :idx="key"
        :strukturovana-podmienka-spec-ref="item?.strukturovanaPodmienkaSpecRef"
        :zoppm-podmienka-spec-ref="zoppmPodmienkaSpecRef"
        :nazov="item?.strukturovanaPodmienkaSpecRef?.nazov"
        :spec-ref-name="props.specRefName"
      />
    </div>
  </template>

  <template v-if="Object.keys(strukturovanePodmienky).length <= 0">
    <ValidationWrapper
      v-slot="slotProps"
      :name="`podmienkyPoskytnutiaPrispevku[${arrayPosition}].vyjadrenie`"
    >
      <IDSKTextarea
        v-bind="slotProps"
        :label="t('zoppm.step2.vyjadrenie')"
        class="mb-8"
      />
    </ValidationWrapper>
    <Prilohy
      :name="`podmienkyPoskytnutiaPrispevku[${arrayPosition}]`"
      :array-position="arrayPosition"
      :idx="0"
      :zoppm-podmienka-spec-ref="zoppmPodmienkaSpecRef"
      :nazov="item?.strukturovanaPodmienkaSpecRef?.nazov"
      :spec-ref-name="props.specRefName"
    />
  </template>
  <IDSKDivider class="mt-8 mb-8" />
</template>

<style lang="scss"></style>
